/* eslint-disable @typescript-eslint/no-explicit-any */
import { REFRESH_TOKEN_KEY } from '@/constants';
import { TokensType } from '@/types';
import { authorizeDreamSDK } from '@/utils/dreamCRM';
import axios, {
  AxiosInstance,
  AxiosResponse,
  AxiosError,
  InternalAxiosRequestConfig,
} from 'axios';

export interface TokenHandlers {
  getTokens: () => Promise<TokensType> | TokensType;
  setTokens: (access: string, refresh: string) => Promise<void> | void;
  removeTokens: () => Promise<void> | void;
}

interface CreateInstanceParams extends TokenHandlers {
  refreshEndpoint: string;
}

export const createInstance = ({
  getTokens,
}: Pick<CreateInstanceParams, 'getTokens'>): AxiosInstance => {
  const instance = axios.create();

  instance.interceptors.request.use(
    async (config: InternalAxiosRequestConfig) => {
      const { accessToken } = await getTokens();
      if (accessToken) {
        if (config.headers && typeof config.headers.set === 'function') {
          config.headers.set('Authorization', `Bearer ${accessToken}`);
        } else {
          (config.headers as any) = {
            ...config.headers,
            Authorization: `Bearer ${accessToken}`,
          };
        }
      }
      return config;
    },
  );

  return instance;
};

export const createPersonalInstance = ({
  getTokens,
  setTokens,
  removeTokens,
  refreshEndpoint,
}: CreateInstanceParams): AxiosInstance => {
  const instance = axios.create();

  instance.interceptors.request.use(
    async (config: InternalAxiosRequestConfig) => {
      const { accessToken } = await getTokens();
      if (accessToken) {
        if (config.headers && typeof config.headers.set === 'function') {
          config.headers.set('Authorization', `Bearer ${accessToken}`);
        } else {
          (config.headers as any) = {
            ...config.headers,
            Authorization: `Bearer ${accessToken}`,
          };
        }
      }
      return config;
    },
  );

  instance.interceptors.response.use(
    (response: AxiosResponse) => response,
    async (error: AxiosError) => {
      const originalRequest = error.config as InternalAxiosRequestConfig & {
        _retry?: boolean;
      };

      if (error.response?.status === 401 && !originalRequest._retry) {
        originalRequest._retry = true;

        try {
          const token = localStorage.getItem(REFRESH_TOKEN_KEY);

          if (!token) {
            throw new Error('No refresh token found');
          }

          const payload = { token };

          const refreshResponse = await axios.post(refreshEndpoint, payload, {
            headers: {
              'Content-Type': 'application/json',
            },
          });

          const { access_token, refresh_token, user } =
            refreshResponse.data.data;
          await setTokens(access_token, refresh_token);
          authorizeDreamSDK(user?.websiteId);

          if (
            originalRequest.headers &&
            typeof originalRequest.headers.set === 'function'
          ) {
            originalRequest.headers.set(
              'Authorization',
              `Bearer ${access_token}`,
            );
          } else {
            (originalRequest.headers as any) = {
              ...originalRequest.headers,
              Authorization: `Bearer ${access_token}`,
            };
          }

          return instance(originalRequest);
        } catch (refreshError) {
          await removeTokens();
          return Promise.reject(refreshError);
        }
      }

      return Promise.reject(error);
    },
  );

  return instance;
};
